<template>
  <div class="co-lead-claim-details">
    <!-- Co-lead Claim Details: the CLAIM-level fields, shared across the whole
         co-lead (one claim = one sanctions check + one country + one cause of
         loss). Sanctions Check lives here (not step 1/2) because it screens the
         INSURED NAME, which is only known once the claim ref is found; step 3 is
         the first clean place the insured name is available. Per-UMR fields
         (broker, state) are handled elsewhere. -->
    <div v-if="activeRow" class="co-lead-claim-details__panel">
      <!-- Top row: claim-level fields, ALL shared across the whole co-lead
           (written to every UMR row). Loss State / SL Tax State show only for a
           USA claim; NAIC Code is always available. -->
      <b-row class="row mb-3">
        <b-col lg="3">
          <!-- Sanctions Check applies to the whole cash call (single value). -->
          <CashCallFormSactionsCheckStatus
              :value="sanctionStatus"
              @input="$emit('update:sanctionStatus', $event)"
          />
        </b-col>
        <b-col lg="3">
          <!-- Claim Country is shared across every UMR on the claim. Full world
               country list with type-ahead + a scrollable, height-capped list. -->
          <label>Claim Country <span class="text-danger">*</span></label>
          <CashCallAutocompleteInput
              :value="selectedCountry"
              name="Claim Country"
              placeholder="Type or select a country"
              :options="countryNames"
              :max-options="1000"
              @input="updateAllRows('loss_country', $event)"
          />
        </b-col>
        <b-col lg="2" v-if="isUsa">
          <CashCallFormState
              :value="activeRow.loss_state"
              @input="updateAllRows('loss_state', $event)"
          />
        </b-col>
        <b-col lg="2" v-if="isUsa">
          <CashCallFormTaxState
              :value="activeRow.loss_sl_state"
              @input="updateAllRows('loss_sl_state', $event)"
          />
        </b-col>
        <b-col lg="2">
          <BaseInput
              :value="activeRow.naic_code"
              @input="updateAllRows('naic_code', $event)"
              label="NAIC Code"
              size="sm"
          />
        </b-col>
      </b-row>

      <b-row class="row mb-3">
        <b-col lg="8">
          <label>
            Cause of Loss
            <b-icon icon="info-circle" v-b-tooltip.hover="'Brief description of the loss event - shared across all UMRs on this claim'" />
          </label>
          <!-- One claim => one Cause of Loss shared across every UMR. -->
          <b-form-textarea
              :value="activeRow.cause_of_loss"
              @input="updateAllRows('cause_of_loss', $event)"
              placeholder="Provide brief claim description"
              rows="3"
              max-rows="3"
          />
        </b-col>
      </b-row>

      <!-- Read-only summary card of the shared claim parties/dates (from the
           first UMR). Lead Underwriter, Broker and the Policy Effective/Expiry
           dates are intentionally NOT shown here (per LIQ layout). -->
      <b-card class="co-lead-claim-details__summary compact-summary">
        <table class="summary-table">
          <tr>
            <th>Insured</th>
            <td>{{ activeRow.insured_name || '—' }}</td>
            <th>TPA</th>
            <td>{{ activeRow.tpa || '—' }}</td>
          </tr>
          <tr>
            <th>Coverholder</th>
            <td>{{ activeRow.coverholder || '—' }}</td>
            <th>Recent Report Date</th>
            <td>{{ activeRow.date_recent_report_period || '—' }}</td>
          </tr>
          <tr>
            <th>Date of Loss</th>
            <td>{{ activeRow.date_of_loss || '—' }}</td>
            <th>Date Claim Made</th>
            <td>{{ activeRow.date_claim_made || '—' }}</td>
          </tr>
        </table>
      </b-card>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from '@vue/composition-api'
import { CASH_CALL_COLEAD_COUNTRY_OPTIONS } from '@constants'
import { BaseInput } from '@components/base'
import CashCallFormSactionsCheckStatus from './CashCallFormSactionsCheckStatus.vue'
import CashCallAutocompleteInput from './CashCallAutocompleteInput.vue'
import CashCallFormState from './CashCallFormState.vue'
import CashCallFormTaxState from './CashCallFormTaxState.vue'

export default defineComponent({
  name: 'CashCallCoLeadClaimDetailsStep',
  components: {
    BaseInput,
    CashCallFormSactionsCheckStatus,
    CashCallAutocompleteInput,
    CashCallFormState,
    CashCallFormTaxState
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    // Sanctions Check for the whole cash call (single shared value). Bound
    // two-way via update:sanctionStatus to the parent's formData.sanction_status.
    sanctionStatus: {
      type: String,
      default: ''
    }
  },
  emits: ['input', 'update:sanctionStatus'],
  setup(props, { emit }) {
    const rows = computed(() => props.value)

    // Map common country-code / spelling variants coming from the claim data to
    // our canonical dropdown values, so e.g. a stored "US" or "United States"
    // selects the existing "USA" option instead of adding a duplicate entry.
    const COUNTRY_ALIASES = {
      US: 'USA', USA: 'USA', 'UNITED STATES': 'USA', 'UNITED STATES OF AMERICA': 'USA',
      GB: 'UK', UK: 'UK', 'UNITED KINGDOM': 'UK', 'GREAT BRITAIN': 'UK',
      CA: 'Canada', CAN: 'Canada', CANADA: 'Canada'
    }
    const canonicalCountry = (value) => {
      if (!value) return ''
      const hit = COUNTRY_ALIASES[String(value).trim().toUpperCase()]
      return hit || value
    }

    // A co-lead is one claim, so Claim Country + Cause of Loss are shared. We
    // read the display value from the first row and write to every row. The
    // country is normalized to its canonical dropdown value so it selects the
    // right option rather than showing a duplicate.
    const activeRow = computed(() => rows.value[0] || null)
    const selectedCountry = computed(() =>
      canonicalCountry(activeRow.value && activeRow.value.loss_country)
    )
    // Loss State / SL Tax State are US-specific, so only show them for a USA
    // claim (mirrors the normal cash call's country-dependent fields).
    const isUsa = computed(() => selectedCountry.value === 'USA')

    // Country suggestions for the autocomplete (plain string list). USA / UK /
    // Canada are pinned to the TOP (common cases), then every other country
    // alphabetically. We build the order here (rather than relying on
    // sort-options, which would re-alphabetize the pinned three back into the
    // list). An unrecognized stored value is prepended so it still appears.
    const TOP_COUNTRIES = ['USA', 'UK', 'Canada']
    const countryNames = computed(() => {
      const all = CASH_CALL_COLEAD_COUNTRY_OPTIONS.map((o) => o.value)
      const rest = all
        .filter((c) => !TOP_COUNTRIES.includes(c))
        .sort((a, b) => a.localeCompare(b))
      const ordered = [...TOP_COUNTRIES, ...rest]
      const current = selectedCountry.value
      if (current && !ordered.includes(current)) return [current, ...ordered]
      return ordered
    })

    // These two fields describe the CLAIM, not an individual UMR, so writing one
    // updates them on every row.
    const updateAllRows = (field, newValue) => {
      const next = rows.value.map((row) => ({ ...row, [field]: newValue }))
      emit('input', next)
    }

    const isBlank = (v) => v === undefined || v === null || String(v).trim() === ''

    const validationAttempted = ref(false)

    // Called by the parent on Save & Next. Claim Country is the only required
    // field on this step now (it applies to the whole co-lead).
    const validateAllRows = () => {
      validationAttempted.value = true
      const row = rows.value[0]
      return row ? !isBlank(row.loss_country) : true
    }

    return {
      rows,
      activeRow,
      selectedCountry,
      isUsa,
      countryNames,
      updateAllRows,
      validationAttempted,
      validateAllRows
    }
  }
})
</script>

<style scoped>
/* Claim-level fields (Sanctions Check + Claim Country + states + Cause of Loss),
   all shared across the whole co-lead. */
.co-lead-claim-details__panel label {
  display: block;
  margin-bottom: 4px;
}

/* Read-only shared-claim summary card, matching the normal cash call's
   compact-summary look EXACTLY (same full-width table sizing). `table-layout:
   fixed` + the 20%/30% column widths force the 4 columns to span the whole card
   (otherwise the browser sizes columns to content and the table collapses to a
   narrow, left-bunched block). */
.co-lead-claim-details__summary.compact-summary {
  background: #fafafa;
  border: 1px solid #ddd;
  font-size: 12px;
  width: 100%;
}
/* Give the card body real padding so the inner grey table sits AWAY from the
   outer card border (a gap at the top/sides), matching the normal cash call.
   Without this the b-card-body collapses to ~0 and the grey table hugs the
   border. ::v-deep reaches BootstrapVue's internal .card-body. */
.co-lead-claim-details__summary.compact-summary ::v-deep .card-body {
  padding: 12px !important;
}
.co-lead-claim-details__summary .summary-table {
  width: 100%;
  table-layout: fixed;
}
.co-lead-claim-details__summary .summary-table th {
  width: 20%;
  text-align: left;
  font-weight: 600;
  padding: 6px 6px;
  background: #f0f0f0;
}
.co-lead-claim-details__summary .summary-table td {
  width: 30%;
  padding: 6px 6px;
}
</style>
